import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {MediaPlanBadge} from '../MediaPlanBadge';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {getMetricName} from '../../dashboard/columns';
import {FORMAT_METRIC_VALUE} from '../../dashboard/metrics';

function formatAmount(amount) {
    const num = parseFloat(amount);
    if (isNaN(num)) return null;
    if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toLocaleString();
}

function formatBudget(currency, amount) {
    if (!amount && amount !== 0) return null;
    const formatted = formatAmount(amount);
    if (formatted === null) return null;
    const prefix = currency === 'USD' ? 'USD ' : currency === 'ARS' ? 'ARS ' : (currency || '') + ' ';
    return prefix + formatted;
}

// Progreso del período (mockup de la ficha v2): gastado / presupuesto efectivo. El color sigue
// la semántica de severidad del design system: verde en curso, ámbar cerca del tope, rojo pasado.
function budgetProgress(bu) {
    const spent = parseFloat(bu.current_budget_cost);
    const total = parseFloat(bu.current_budget_total !== null && bu.current_budget_total !== undefined
        ? bu.current_budget_total : bu.allocated_budget);
    if (isNaN(spent) || isNaN(total) || total <= 0) return null;
    const ratio = spent / total;
    return {
        spent,
        total,
        pct: Math.min(100, Math.round(ratio * 100)),
        tone: ratio > 1 ? '-over' : ratio >= 0.85 ? '-warn' : '-good',
    };
}

function getFirstGoal(bu) {
    const goals = bu.goals_data;
    if (!goals || !goals.business_unit_goals || goals.business_unit_goals.length === 0) return null;
    const sorted = [...goals.business_unit_goals].sort((a, b) => (a.priority || 99) - (b.priority || 99));
    return sorted[0];
}

export function BUSummaryRow({bu}) {
    const {t} = useTranslation();

    const channels = bu.bucc_channels || [];
    const budgetText = formatBudget(bu.budget_currency, bu.allocated_budget);
    const progress = budgetProgress(bu);
    const goal = getFirstGoal(bu);

    return (
        <tr className="bu-summary-row">
            <td>
                <div className="bu-summary-name">
                    <div className="bu-summary-channels">
                        {channels.map((ch, i) => (
                            <ChannelLogo key={i} channel={ch} height={14} width={14} hspace={0} vspace={0}/>
                        ))}
                    </div>
                    <Link to={`/business-unit/${bu.id}/`}>{bu.name}</Link>
                    {bu.from_media_plan && <MediaPlanBadge/>}
                </div>
            </td>
            <td>
                {progress ? (
                    <div className="bu-summary-budget-cell">
                        <span className="bu-summary-budget">
                            {formatBudget(bu.budget_currency, progress.spent)} / {formatAmount(progress.total)}
                        </span>
                        <div className="bu-progress">
                            <div className={`bu-progress-fill ${progress.tone}`} style={{width: `${progress.pct}%`}}/>
                        </div>
                    </div>
                ) : budgetText ? (
                    <span className="bu-summary-budget">{budgetText}</span>
                ) : (
                    <span className="bu-summary-no-budget">{t('control_central.bu_no_budget')}</span>
                )}
            </td>
            <td>
                {goal ? (
                    <span className="bu-summary-goal">
                        {getMetricName(t, goal.metric_name)} = {FORMAT_METRIC_VALUE(goal.metric_name, goal.value)}
                    </span>
                ) : (
                    <span className="bu-summary-no-goal">&mdash;</span>
                )}
            </td>
        </tr>
    );
}
